import React, {useEffect, useState} from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography
} from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {useNavigate} from 'react-router';
import {useSession} from '../../../../firebase/UserProvider';
const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() { const t = localStorage.getItem('access_token'); return t ? {Authorization: `Bearer ${t}`, 'Content-Type': 'application/json'} : {'Content-Type': 'application/json'}; }

// ReadbookHeader: back button, editable title, delete
const ReadbookHeader = ({readbookId, title, onTitleUpdated}) => {
    const navigate = useNavigate();
    const {user} = useSession() || {};
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(title ?? '');
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (!editing) setValue(title ?? '');
    }, [title, editing]);

    const canEdit = Boolean(user);

    const handleBack = () => navigate('/readbooks');

    const commit = async () => {
        const next = (value ?? '').toString();
        const changed = (title ?? '') !== next;
        if (changed && readbookId) {
            try {
                if (typeof onTitleUpdated === 'function') onTitleUpdated(next);
                setEditing(false);
                await fetch(`${_awpBase}/api/readbooks/${encodeURIComponent(readbookId)}`, {
                    method: 'PATCH', headers: _authH(), body: JSON.stringify({title: next}),
                });
            } catch (err) {
                console.error('Failed to update title', err);
            }
        } else {
            setEditing(false);
        }
    };

    const cancel = () => {
        setValue(title ?? '');
        setEditing(false);
    };

    const handleDelete = async () => {
        if (!readbookId) return;
        setDeleting(true);
        try {
            await fetch(`${_awpBase}/api/readbooks/${encodeURIComponent(readbookId)}`, {method: 'DELETE', headers: _authH()});
            setConfirmOpen(false);
            navigate('/readbooks');
        } catch (err) {
            console.error('Failed to delete readbook', err);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <>
            <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} spacing={1}
                   boxSizing='border-box' p={1} py={1} bgcolor={'white'}>
                <Stack direction={'row'} alignItems={'center'} spacing={1} flexGrow={1}>
                    <IconButton onClick={handleBack}><ArrowBackOutlinedIcon/></IconButton>

                    {editing ? (
                        <TextField
                            variant="outlined"
                            autoFocus
                            fullWidth
                            size={'small'}
                            value={value}
                            onChange={(e) => setValue(e.target.value)}
                            onBlur={commit}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') commit();
                                if (e.key === 'Escape') cancel();
                            }}
                            placeholder="Enter a readbook title"
                        />
                    ) : (
                        <Typography
                            variant="h6"
                            onClick={canEdit ? () => setEditing(true) : undefined}
                            sx={{
                                cursor: canEdit ? 'pointer' : 'default',
                                '&:hover': canEdit ? {bgcolor: (theme) => theme.palette.action.hover} : undefined
                            }}
                            title={canEdit ? 'Click to edit' : undefined}
                        >
                            {title || 'Untitled readbook'}
                        </Typography>
                    )}
                </Stack>

                <Stack direction={'row'} alignItems={'center'} spacing={1}>
                    <Button variant="text" onClick={() => setConfirmOpen(true)} startIcon={<DeleteOutlineIcon/>}
                            color="error">Delete</Button>
                </Stack>
            </Stack>

            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Are you sure?</DialogTitle>
                <DialogContent>
                    <Stack spacing={2}>
                        <Typography variant="body1">This action will permanently delete "{title || ''}".</Typography>
                        <Alert severity={'warning'}>This cannot be undone.</Alert>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)} disabled={deleting}>Cancel</Button>
                    <Button color="error" onClick={handleDelete}
                            disabled={deleting}>{deleting ? 'Deleting…' : 'Delete'}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ReadbookHeader;
